import React from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { AssistantTurn, AssistantConversationStatus } from '@models/assistant.model';
import { useTranslation } from '@hooks/useTranslation';
import { getAssistantTurnErrorMessage } from '@hooks/api/assistant/assistantErrorMessage';
import { MarkdownMessage } from './MarkdownMessage';

interface MessageListProps {
  turns: AssistantTurn[];
  status?: AssistantConversationStatus;
  errorCode?: string;
  onRetry: () => void;
}

/** Sprint 32, story-165. Renders `AssistantTurnDto[]`: user/assistant bubbles, the muted "Looked
 * at: …" line from `toolsUsed`, `ACTION_RESULT`/`ACTION_DISMISSED` as centered system lines, and a
 * `FAILED` banner + Retry. */
export const MessageList: React.FC<MessageListProps> = ({ turns, status, errorCode, onRetry }) => {
  const t = useTranslation('assistant');

  return (
    <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="assistant-message-list">
      {turns.map((turn, idx) => {
        if (turn.kind === 'ACTION_RESULT' || turn.kind === 'ACTION_DISMISSED') {
          return (
            <div key={idx} className="text-center text-xs text-gray-500 dark:text-gray-400 italic">
              {turn.kind === 'ACTION_RESULT'
                ? t.actionExecuted.replace('{summary}', turn.text)
                : t.actionDismissed.replace('{type}', turn.text.replace(/^Dismissed:\s*/, ''))}
            </div>
          );
        }

        if (!turn.text) return null;

        return (
          <div key={idx} className="text-sm text-gray-600 dark:text-gray-400">
            <div className="font-semibold text-gray-700 dark:text-gray-300 mb-0.5">
              {turn.role === 'USER' ? t.youLabel : t.assistantLabel}
            </div>
            {turn.role === 'ASSISTANT' ? (
              <MarkdownMessage text={turn.text} />
            ) : (
              <span className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{turn.text}</span>
            )}
            {turn.role === 'ASSISTANT' && turn.toolsUsed.length > 0 && (
              <div className="text-[10px] text-gray-400 mt-0.5">
                {t.lookedAt.replace('{tools}', turn.toolsUsed.join(', '))}
              </div>
            )}
          </div>
        );
      })}

      {status === 'THINKING' && (
        <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="assistant-thinking">
          <Loading size="sm" />
          <span>{t.thinking}</span>
        </div>
      )}

      {status === 'FAILED' && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 p-3 flex items-start gap-2"
          data-testid="assistant-error-banner">
          <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} className="text-red-500 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-red-700 dark:text-red-300">
              {getAssistantTurnErrorMessage(errorCode, t)}
            </p>
            <Button type="button" variant="secondary" size="md" className="mt-2" onClick={onRetry}>
              {t.retry}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;
